/**
 * StudentResponseV2 - Student Produced Response (SPR) input component
 *
 * Features:
 * - Text input for free-form answers
 * - Debounced auto-save
 * - Visual feedback for answer state
 * - Keyboard submit support
 */

import { memo, useState, useEffect, useRef, useCallback } from 'react'
import { useChallengeV2 as useChallengeStoreV2 } from '../../../hooks/useChallenge'
import { useDebounce } from '../../../hooks/useDebounce'

const StudentResponseV2 = memo(() => {
  // Store state
  const textAnswer = useChallengeStoreV2((state) => state.textAnswer)

  // Store actions
  const setTextAnswer = useChallengeStoreV2((state) => state.setTextAnswer)
  const flushSave = useChallengeStoreV2((state) => state.flushSave)

  // Local state for immediate UI feedback
  const [localValue, setLocalValue] = useState(textAnswer || '')
  const debouncedValue = useDebounce(localValue, 1500)
  const lastSavedRef = useRef(textAnswer || '')

  // Sync local value when store value changes (hydration)
  useEffect(() => {
    setLocalValue(textAnswer || '')
    lastSavedRef.current = textAnswer || ''
  }, [textAnswer])

  // Save debounced value to store
  useEffect(() => {
    if (debouncedValue !== lastSavedRef.current) {
      setTextAnswer(debouncedValue)
      lastSavedRef.current = debouncedValue
    }
  }, [debouncedValue, setTextAnswer])

  // Handle input change
  const handleChange = useCallback((e) => {
    setLocalValue(e.target.value)
  }, [])

  // Handle blur - flush save
  const handleBlur = useCallback(() => {
    if (localValue !== lastSavedRef.current) {
      setTextAnswer(localValue)
      lastSavedRef.current = localValue
      flushSave()
    }
  }, [localValue, setTextAnswer, flushSave])

  // Handle Enter key
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        handleBlur()
      }
    },
    [handleBlur]
  )

  const hasAnswer = localValue && localValue.trim().length > 0
  const isTyping = localValue !== debouncedValue

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
          Your Answer
        </h3>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          Type your answer and press Enter
        </span>
      </div>

      <div
        className={`
          relative rounded-xl border-2 transition-all duration-200
          ${
            hasAnswer
              ? 'border-emerald-400 dark:border-emerald-600 bg-emerald-50 dark:bg-emerald-900/20'
              : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
          }
          focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-500/20
        `}
      >
        <input
          type="text"
          value={localValue}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder="Enter your answer here..."
          className={`
            w-full px-4 py-4 text-lg font-medium
            bg-transparent outline-none
            placeholder-gray-400 dark:placeholder-gray-500
            text-gray-900 dark:text-white
          `}
          aria-label="Your answer"
        />

        {/* Status indicators */}
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
          {isTyping && (
            <span className="flex items-center gap-1 text-xs text-indigo-500 dark:text-indigo-400">
              <span className="w-1.5 h-1.5 bg-indigo-500 rounded-full animate-pulse"></span>
              Typing...
            </span>
          )}

          {hasAnswer && !isTyping && (
            <span className="flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
              Saved
            </span>
          )}
        </div>
      </div>

      {/* Helper text */}
      <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
        <span>For math answers, use numbers (e.g., 403, 12.5, -8)</span>
        <span>{localValue.length} characters</span>
      </div>
    </div>
  )
})

StudentResponseV2.displayName = 'StudentResponseV2'

export default StudentResponseV2
